import React, { useState, useEffect } from 'react';
import { Card, Badge, Spinner } from 'react-bootstrap';
import { FaChevronLeft, FaChevronRight, FaBirthdayCake, FaMedal } from 'react-icons/fa';
import { getReminders } from '../../services/firebase/reminderService';
import { toast } from 'react-toastify';
import './ReminderCalendar.css';

const ReminderCalendar = () => {
    const [currentDate, setCurrentDate] = useState(new Date());
    const [reminders, setReminders] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedDay, setSelectedDay] = useState(() => {
        const today = new Date();
        return today.getDate();
    });

    useEffect(() => {
        fetchMonthlyReminders();
    }, [currentDate]);

    const fetchMonthlyReminders = async () => {
        setLoading(true);
        try {
            // Fetch reminders for the whole year to show in calendar
            const response = await getReminders(365, null);
            setReminders(response.data || []);
        } catch (error) {
            console.error('Error fetching monthly reminders:', error);
            toast.error('Failed to fetch reminders');
        } finally {
            setLoading(false);
        }
    };

    const getDaysInMonth = (date) => {
        const year = date.getFullYear();
        const month = date.getMonth();
        return new Date(year, month + 1, 0).getDate();
    };

    const getFirstDayOfMonth = (date) => {
        const year = date.getFullYear();
        const month = date.getMonth();
        return new Date(year, month, 1).getDay();
    };

    const getRemindersForDate = (day) => {
        // Create date for comparison
        const targetDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
        targetDate.setHours(0, 0, 0, 0);
        
        return reminders.filter(r => {
            if (!r.date) return false;
            const reminderDate = new Date(r.date);
            return reminderDate.getMonth() === targetDate.getMonth() && reminderDate.getDate() === targetDate.getDate();
        });
    };

    const previousMonth = () => {
        const newDate = new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1);
        setCurrentDate(newDate);
        const today = new Date();
        setSelectedDay(newDate.getMonth() === today.getMonth() && newDate.getFullYear() === today.getFullYear() ? today.getDate() : 1);
    };

    const nextMonth = () => {
        const newDate = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1);
        setCurrentDate(newDate);
        const today = new Date();
        setSelectedDay(newDate.getMonth() === today.getMonth() && newDate.getFullYear() === today.getFullYear() ? today.getDate() : 1);
    };

    const goToToday = () => {
        const today = new Date();
        setCurrentDate(today);
        setSelectedDay(today.getDate());
    };

    const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'];
    const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

    const daysInMonth = getDaysInMonth(currentDate);
    const firstDay = getFirstDayOfMonth(currentDate);

    // Create array of days
    const days = [];
    for (let i = 0; i < firstDay; i++) {
        days.push(null);
    }
    for (let i = 1; i <= daysInMonth; i++) {
        days.push(i);
    }

    const selectedReminders = selectedDay ? getRemindersForDate(selectedDay) : [];

    return (
        <Card className="reminder-calendar shadow-sm border-0">
            <Card.Body className="p-2 p-sm-3 p-md-4">
                <div className="calendar-header">
                    <div className="calendar-nav-group">
                        <button className="nav-btn" onClick={previousMonth} title="Previous month">
                            <FaChevronLeft />
                        </button>
                        <button className="nav-btn" onClick={nextMonth} title="Next month">
                            <FaChevronRight />
                        </button>
                    </div>
                    <h4 className="month-year">
                        {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
                    </h4>
                    <button 
                        className="btn btn-sm btn-outline-primary today-btn"
                        onClick={goToToday}
                    >
                        Today
                    </button>
                </div>

                {loading ? (
                    <div className="text-center py-5">
                        <Spinner animation="border" variant="primary" />
                    </div>
                ) : (
                    <>
                        <div className="calendar-grid">
                            {/* Day headers */}
                            {dayNames.map(day => (
                                <div key={day} className="day-header">
                                    <span className="d-none d-sm-inline">{day}</span>
                                    <span className="d-inline d-sm-none">{day[0]}</span>
                                </div>
                            ))}

                            {/* Calendar days */}
                            {days.map((day, index) => {
                                const dayReminders = day ? getRemindersForDate(day) : [];
                                const isToday = day &&
                                    day === new Date().getDate() &&
                                    currentDate.getMonth() === new Date().getMonth() &&
                                    currentDate.getFullYear() === new Date().getFullYear();
                                const isSelected = day && day === selectedDay;

                                return (
                                    <div
                                        key={index}
                                        className={`calendar-day ${!day ? 'empty' : ''} ${isToday ? 'today' : ''} ${isSelected ? 'selected' : ''} ${dayReminders.length > 0 ? 'has-reminder' : ''}`}
                                        onClick={() => day && setSelectedDay(day)}
                                        style={{ cursor: day ? 'pointer' : 'default' }}
                                    >
                                        {day && (
                                            <>
                                                <div className="day-number">{day}</div>
                                                {dayReminders.length > 0 && (
                                                    <div className="reminders-list">
                                                        {dayReminders.map((reminder, idx) => (
                                                            <div
                                                                key={idx}
                                                                className={`reminder-item ${reminder.type}`}
                                                                title={reminder.message}
                                                            >
                                                                {reminder.type === 'birthday' ? (
                                                                    <FaBirthdayCake className="reminder-icon" />
                                                                ) : (
                                                                    <FaMedal className="reminder-icon" />
                                                                )}
                                                                <span className="reminder-name">
                                                                    {reminder.employeeName}
                                                                </span>
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                            </>
                                        )}
                                    </div>
                                );
                            })}
                        </div>

                        {/* Selected Day Event Details (Essential on Mobile!) */}
                        {selectedDay && (
                            <div className="selected-day-panel mt-3 p-3 rounded-3">
                                <div className="d-flex justify-content-between align-items-center mb-2">
                                    <div className="fw-bold text-dark d-flex align-items-center gap-2" style={{ fontSize: '0.88rem' }}>
                                        <span>📅 {selectedDay} {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}</span>
                                        {selectedDay === new Date().getDate() &&
                                         currentDate.getMonth() === new Date().getMonth() &&
                                         currentDate.getFullYear() === new Date().getFullYear() && (
                                            <Badge bg="success" style={{ fontSize: '0.65rem' }}>Today</Badge>
                                        )}
                                    </div>
                                    {selectedReminders.length > 0 ? (
                                        <Badge bg="primary" pill style={{ fontSize: '0.72rem' }}>
                                            {selectedReminders.length} {selectedReminders.length === 1 ? 'Event' : 'Events'}
                                        </Badge>
                                    ) : (
                                        <span className="text-muted" style={{ fontSize: '0.75rem' }}>No events</span>
                                    )}
                                </div>

                                {selectedReminders.length === 0 ? (
                                    <p className="text-muted mb-0 small" style={{ fontSize: '0.78rem' }}>
                                        No birthdays or work anniversaries on this date. Tap any highlighted date to view event details.
                                    </p>
                                ) : (
                                    <div className="d-flex flex-column gap-2">
                                        {selectedReminders.map((reminder, idx) => (
                                            <div 
                                                key={idx}
                                                className={`selected-event-card p-2 p-sm-3 rounded-3 d-flex align-items-center justify-content-between ${reminder.type}`}
                                            >
                                                <div className="d-flex align-items-center gap-2">
                                                    <div className={`event-icon-circle ${reminder.type}`}>
                                                        {reminder.type === 'birthday' ? <FaBirthdayCake /> : <FaMedal />}
                                                    </div>
                                                    <div>
                                                        <div className="fw-bold text-dark" style={{ fontSize: '0.85rem' }}>
                                                            {reminder.employeeName}
                                                        </div>
                                                        <div className="text-muted small" style={{ fontSize: '0.75rem' }}>
                                                            {reminder.type === 'birthday' 
                                                                ? '🎂 Birthday Celebration' 
                                                                : `🏅 Work Anniversary (${reminder.years ? `${reminder.years} Years` : 'Anniversary'})`}
                                                        </div>
                                                    </div>
                                                </div>
                                                <Badge 
                                                    bg={reminder.type === 'birthday' ? 'info' : 'warning'} 
                                                    text="dark" 
                                                    className="px-2 py-1"
                                                    style={{ fontSize: '0.7rem' }}
                                                >
                                                    {reminder.type === 'birthday' ? 'Birthday' : 'Anniversary'}
                                                </Badge>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}
                    </>
                )}

                {/* Legend */}
                <div className="calendar-legend mt-3">
                    <div className="legend-item">
                        <FaBirthdayCake className="text-info me-2" />
                        <span>Birthday</span>
                    </div>
                    <div className="legend-item">
                        <FaMedal className="text-warning me-2" />
                        <span>Work Anniversary</span>
                    </div>
                </div>
            </Card.Body>
        </Card>
    );
};

export default ReminderCalendar;
